/**
 * Selector "PLANTILLA" para los procesos que generan documentos PDF.
 *
 * - Consulta las plantillas ACTIVAS del tipo en el alcance del usuario
 *   (GET /document-templates/available/{tipo}).
 * - Si hay una sola, se usa sin mostrar selector; si hay varias, muestra el selector
 *   con la predeterminada preseleccionada.
 * - La elección queda disponible para `withTemplate(url, tipo)`, que agrega
 *   `template_id` a la URL del PDF (los endpoints lo aceptan como opcional).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const selected = {};
const cache = {};

export function selectedTemplateId(tipo) {
  return selected[tipo] || null;
}

/** Agrega `template_id` (si hay una plantilla elegida para el tipo) a una URL de PDF. */
export function withTemplate(url, tipo) {
  const tid = selected[tipo];
  if (!tid) return url;
  return `${url}${url.includes("?") ? "&" : "?"}template_id=${encodeURIComponent(tid)}`;
}

export default function TemplatePicker({ tipo, className = "", onChange, label = "PLANTILLA",
  selectClassName = "bg-card border border-border rounded px-2 py-1 text-xs" }) {
  const [items, setItems] = useState(cache[tipo] || []);
  const [value, setValue] = useState(selected[tipo] || "");

  useEffect(() => {
    let alive = true;
    api.get(`/document-templates/available/${tipo}`)
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        cache[tipo] = list;
        setItems(list);
        const keep = list.find((t) => t.id === selected[tipo]);
        const def = keep || list.find((t) => t.predeterminada_efectiva) || list[0];
        selected[tipo] = def ? def.id : null;
        setValue(def ? def.id : "");
        if (onChange) onChange(selected[tipo]);
      })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  if (items.length < 2) return null;
  return (
    <label className={`inline-flex items-center gap-2 text-xs ${className}`} data-testid={`template-picker-${tipo}`}>
      <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => {
          selected[tipo] = e.target.value || null;
          setValue(e.target.value);
          if (onChange) onChange(selected[tipo]);
        }}
        className={selectClassName}
        data-testid={`template-picker-select-${tipo}`}
      >
        {items.map((t) => (
          <option key={t.id} value={t.id}>
            {t.nombre}{t.version ? ` (v${t.version})` : ""}{t.predeterminada_efectiva ? " — predeterminada" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}
